import { useEffect, useState } from "react";
import type { AttendeeEntryDecision, InvitedEntryDecision } from "@verity-connect/contracts";
import { JoinPage, type JoinAudience, type JoinSubmission } from "./JoinPage";
import { deviceAlreadyEnrolled, deviceClientId, enrolPasskey, passkeysAvailable, signInWithPasskey, type PasskeyDoor } from "./usePasskeys";

interface School { id: string; name: string }

export type JoinEntryDecision = AttendeeEntryDecision | InvitedEntryDecision;

const DOORS: Record<JoinAudience, { entry: string; join: string; code: string | null; verify: string | null; landing: string; passkey: PasskeyDoor | null }> = {
  attendee: { entry: "/api/attendee/entry", join: "/api/attendee/join", code: null, verify: null, landing: "/", passkey: "attendee" },
  staff: { entry: "/api/organizer/entry", join: "/api/organizer/join", code: "/api/organizer/auth/code", verify: "/api/organizer/auth/verify", landing: "/", passkey: "organizer" },
  presenter: { entry: "/api/presenter/entry", join: "/api/presenter/join", code: "/api/presenter/auth/code", verify: "/api/presenter/auth/verify", landing: "/", passkey: "presenter" },
  school: { entry: "/api/school/entry", join: "/api/school/join", code: "/api/school/auth/code", verify: "/api/school/auth/verify", landing: "/", passkey: "school" },
  exhibitor: { entry: "/api/exhibitor/entry", join: "/api/exhibitor/join", code: "/api/exhibitor/auth/code", verify: "/api/exhibitor/auth/verify", landing: "/", passkey: "exhibitor" },
};

async function messageFrom(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as { message?: unknown } | null;
  return typeof body?.message === "string" && body.message ? body.message : fallback;
}

export function JoinRoute({ audience }: { audience: JoinAudience }) {
  const [schools, setSchools] = useState<readonly School[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const door = DOORS[audience];

  useEffect(() => {
    if (audience !== "attendee") return;
    let cancelled = false;
    void fetch("/api/public/organizations", { headers: { accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : { organizations: [] }))
      .then((body: { organizations?: School[] }) => {
        if (!cancelled) setSchools(body.organizations ?? []);
      })
      .catch(() => { if (!cancelled) setSchools([]); });
    return () => { cancelled = true; };
  }, [audience]);

  async function resolveEmail(email: string, turnstileToken: string): Promise<JoinEntryDecision> {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(door.entry, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email, "cf-turnstile-response": turnstileToken }),
      });
      if (!response.ok) throw new Error(await messageFrom(response, "That email could not be checked. Try again."));
      return await response.json() as JoinEntryDecision;
    } finally {
      setBusy(false);
    }
  }

  async function submit(submission: JoinSubmission) {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(door.join, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(
          submission.audience === "attendee"
            ? { ...submission, clientId: deviceClientId(), "cf-turnstile-response": submission.turnstileToken }
            : { email: submission.email, password: submission.password, "cf-turnstile-response": submission.turnstileToken },
        ),
      });
      if (!response.ok) {
        setError(await messageFrom(response, "That account could not be opened. Try again."));
        return;
      }
      if (door.passkey && passkeysAvailable()) setCompleted(true);
      else window.location.assign(door.landing);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "We could not reach the application. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function usePasskey() {
    if (!door.passkey) return;
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey(deviceClientId(), door.passkey);
      window.location.assign(door.landing);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That device sign-in did not work. Try another way.");
    } finally {
      setBusy(false);
    }
  }

  async function addDevice() {
    if (!door.passkey) return window.location.assign(door.landing);
    setBusy(true); setError(null);
    try {
      await enrolPasskey(door.passkey);
      window.location.assign(door.landing);
    } catch (caught) {
      // Already remembered is what was asked for, not a failure to deliver it.
      if (deviceAlreadyEnrolled(caught)) return window.location.assign(door.landing);
      setError(caught instanceof Error ? caught.message : "This device could not be added. You can continue without it.");
    } finally { setBusy(false); }
  }


  return (
    <JoinPage
      audience={audience}
      schools={schools}
      onResolveEmail={resolveEmail}
      onSubmit={submit}
      onUsePasskey={door.passkey ? usePasskey : null}
      completed={completed}
      onAddDevice={addDevice}
      onSkipDevice={() => window.location.assign(door.landing)}
      busy={busy}
      error={error}
      onClearError={() => setError(null)}
    />
  );
}
